import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Navbar as BSNavbar, 
  Nav, 
  Container, 
  Button, 
  NavDropdown,
  Offcanvas
} from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';

// Define valid role types
type ValidRole = 'admin' | 'business' | 'affiliate' | 'customer';
const defaultRole: ValidRole = 'customer';

const Navbar: React.FC = () => {
  const { isAuthenticated, user, role, logout } = useAuth();
  const navigate = useNavigate();
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  
  // Debug: log user and role
  console.log("user:", user, "role:", role);
  
  const handleLogout = () => {
    logout();
    navigate('/');
    setShowOffcanvas(false);
  };
  
  const handleCloseOffcanvas = () => setShowOffcanvas(false);

  // Helper function to get valid role
  const getValidRole = (role: string | null | undefined): ValidRole => {
    if (!role || !['admin', 'business', 'affiliate', 'customer'].includes(role)) {
      return defaultRole;
    }
    return role as ValidRole;
  };

  // Get navigation items based on user role
  const getMainNavItems = () => {
    if (!isAuthenticated) return null;

    const baseItems = [
      <Nav.Link key="campaign-creator" as={Link} to="/campaign-creator" onClick={handleCloseOffcanvas}>
          Campaign Creator
        </Nav.Link>,
      <Nav.Link key="purchases" as={Link} to="/purchases">
        Purchased Cards
      </Nav.Link>,
      <Nav.Link key="cashout" as={Link} to="/cashout">
        Cashout   
      </Nav.Link>,
      <Nav.Link key="admincashouts" as={Link} to="/admincashouts">
        Admin Cashouts   
      </Nav.Link>,
      <Nav.Link key="dashboard" as={Link} to="/dashboard">
        Dashboard
      </Nav.Link>,
      <Nav.Link key="user-campaigns" as={Link} to="/user-campaigns">
        User Campaigns
      </Nav.Link>
    ];

    const roleItems = {
      admin: [
        <Nav.Link key="users" as={Link} to="/dashboard/users">
          Users
        </Nav.Link>,
        <Nav.Link key="businesses" as={Link} to="/dashboard/businesses">
          Businesses
        </Nav.Link>,
        <Nav.Link key="affiliates" as={Link} to="/dashboard/affiliates">
          Affiliates
        </Nav.Link>,
        <NavDropdown key="admin-tools" title="Admin Tools" id="admin-tools-dropdown">
          <NavDropdown.Item as={Link} to="/system/registration-field-editor">
            Registration Field Config
          </NavDropdown.Item>
          <NavDropdown.Item as={Link} to="/campaign-creator">
            Campaign Creator
          </NavDropdown.Item>
          <NavDropdown.Item as={Link} to="/content-editor">
            Content Editor
          </NavDropdown.Item>
          <NavDropdown.Item as={Link} to="/dashboard/system-settings">
            System Settings
          </NavDropdown.Item>
        </NavDropdown>
      ],
      business: [
        <Nav.Link key="campaigns" as={Link} to="/dashboard/campaigns">
          Campaigns
        </Nav.Link>,
        <Nav.Link key="storefronts" as={Link} to="/dashboard/storefronts">
          Storefronts
        </Nav.Link>,
        <Nav.Link key="inventory" as={Link} to="/dashboard/inventory">
          Inventory
        </Nav.Link>
      ],
      affiliate: [
        <Nav.Link key="referrals" as={Link} to="/dashboard/referrals">
          Referrals
        </Nav.Link>,
        <Nav.Link key="commissions" as={Link} to="/dashboard/commissions">
          Commissions
        </Nav.Link>
      ],
      customer: [
        <Nav.Link key="my-cards" as={Link} to="/dashboard/my-cards">
          My Cards
        </Nav.Link>,
        <Nav.Link key="marketplace" as={Link} to="/dashboard/marketplace">
          Marketplace
        </Nav.Link>
      ]
    };

    const currentRole = getValidRole(role);
    return [...baseItems, ...(roleItems[currentRole] || [])];
  };

  const getMobileNavItems = () => {
    if (!isAuthenticated) {
      return [
        <Nav.Link key="home" as={Link} to="/" onClick={handleCloseOffcanvas}>
          Home
        </Nav.Link>,
        <Nav.Link key="login" as={Link} to="/login" onClick={handleCloseOffcanvas}>
          Login
        </Nav.Link>,
        <Nav.Link key="register" as={Link} to="/register" onClick={handleCloseOffcanvas}>
          Register
        </Nav.Link>
      ];
    }

    const baseItems = [
      <Nav.Link key="dashboard" as={Link} to="/dashboard" onClick={handleCloseOffcanvas}>
        Dashboard
      </Nav.Link>
    ];

    const roleItems = {
      admin: [
        <Nav.Link key="users" as={Link} to="/dashboard/users" onClick={handleCloseOffcanvas}>
          Users
        </Nav.Link>,
        <Nav.Link key="businesses" as={Link} to="/dashboard/businesses" onClick={handleCloseOffcanvas}>
          Businesses
        </Nav.Link>,
        <Nav.Link key="affiliates" as={Link} to="/dashboard/affiliates" onClick={handleCloseOffcanvas}>
          Affiliates
        </Nav.Link>,
        <Nav.Link key="system" as={Link} to="/dashboard/system" onClick={handleCloseOffcanvas}>
          System
        </Nav.Link>,
        // Admin tools section
        <h6 key="admin-tools-header" className="dropdown-header">Admin Tools</h6>,
        <Nav.Link key="reg-field-config" as={Link} to="/system/registration-field-editor" onClick={handleCloseOffcanvas}>
          Registration Field Config
        </Nav.Link>,
        <Nav.Link key="content-editor" as={Link} to="/content-editor" onClick={handleCloseOffcanvas}>
          Content Editor
        </Nav.Link>,
        <Nav.Link key="campaign-creator" as={Link} to="/campaign-creator" onClick={handleCloseOffcanvas}>
          Campaign Creator
        </Nav.Link>,
        <Nav.Link key="system-settings" as={Link} to="/dashboard/system-settings" onClick={handleCloseOffcanvas}>
          System Settings
        </Nav.Link>
      ],
      business: [
        <Nav.Link key="campaigns" as={Link} to="/dashboard/campaigns" onClick={handleCloseOffcanvas}>
          Campaigns
        </Nav.Link>,
        <Nav.Link key="storefronts" as={Link} to="/dashboard/storefronts" onClick={handleCloseOffcanvas}>
          Storefronts
        </Nav.Link>,
        <Nav.Link key="inventory" as={Link} to="/dashboard/inventory" onClick={handleCloseOffcanvas}>
          Inventory
        </Nav.Link>
      ],
      affiliate: [
        <Nav.Link key="referrals" as={Link} to="/dashboard/referrals" onClick={handleCloseOffcanvas}>
          Referrals
        </Nav.Link>,
        <Nav.Link key="commissions" as={Link} to="/dashboard/commissions" onClick={handleCloseOffcanvas}>
          Commissions
        </Nav.Link>
      ],
      customer: [
        <Nav.Link key="my-cards" as={Link} to="/dashboard/my-cards" onClick={handleCloseOffcanvas}>
          My Cards
        </Nav.Link>,
        <Nav.Link key="marketplace" as={Link} to="/dashboard/marketplace" onClick={handleCloseOffcanvas}>
          Marketplace
        </Nav.Link>
      ]
    };

    const userItems = [
      <hr key="divider" className="my-3" />,
      <Nav.Link key="profile" as={Link} to="/dashboard/profile" onClick={handleCloseOffcanvas}>
        Profile
      </Nav.Link>,
      <Nav.Link key="settings" as={Link} to="/dashboard/settings" onClick={handleCloseOffcanvas}>
        Account Settings
      </Nav.Link>,
      <Nav.Link key="logout" onClick={handleLogout} style={{ cursor: 'pointer' }}>
        Logout
      </Nav.Link>
    ];

    const currentRole = getValidRole(role);
    return [
      ...baseItems, 
      ...(roleItems[currentRole] || []),
      ...userItems
    ];
  };

  return (
    <>
      <BSNavbar bg="primary" variant="dark" expand="lg" className="mb-0">
        <Container>
          {/* Brand */}
          <BSNavbar.Brand as={Link} to="/" className="fw-bold">
            Gift Card Exchange
          </BSNavbar.Brand>

          {/* Mobile toggle */}
          <BSNavbar.Toggle 
            aria-controls="responsive-navbar-nav"
            onClick={() => setShowOffcanvas(true)}
          />

          {/* Desktop Navigation */}
          <BSNavbar.Collapse id="responsive-navbar-nav">
            {/* Main navigation items */}
            <Nav className="me-auto">
              {getMainNavItems()}
            </Nav>

            {/* User section */}
            <Nav>
              {isAuthenticated ? (
                <NavDropdown 
                  title={
                    <span>
                      <i className="bi bi-person-circle me-1"></i>
                      {user?.first_name || user?.username || 'User'}
                    </span>
                  }
                  id="user-dropdown"
                  align="end"
                >
                  <NavDropdown.Header>
                    <div className="fw-bold">
                      {user?.first_name ? `${user.first_name} ${user.last_name}` : user?.username}
                    </div>
                    <small className="text-muted text-capitalize">{getValidRole(role)} Account</small>
                  </NavDropdown.Header>
                  <NavDropdown.Divider />
                  <NavDropdown.Item as={Link} to="/dashboard/profile">
                    <i className="bi bi-person me-2"></i>Profile
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/dashboard/settings">
                    <i className="bi bi-gear me-2"></i>Settings
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={handleLogout}>
                    <i className="bi bi-box-arrow-right me-2"></i>Logout
                  </NavDropdown.Item>
                </NavDropdown>
              ) : (
                <div className="d-flex gap-2">
                  <Button
                    as={Link as any}
                    to="/login"
                    variant="outline-light"
                    size="sm"
                  >
                    Login
                  </Button>
                  <Button
                    as={Link as any}
                    to="/register"
                    variant="light"
                    size="sm"
                  >
                    Register
                  </Button>
                </div>
              )}
            </Nav>
          </BSNavbar.Collapse>
        </Container>
      </BSNavbar>

      {/* Mobile Sidebar */}
      <Offcanvas 
        show={showOffcanvas} 
        onHide={handleCloseOffcanvas} 
        placement="start"
        backdrop={true}
        scroll={false}
      >
        <Offcanvas.Header closeButton className="bg-primary text-white">
          <Offcanvas.Title>
            <i className="bi bi-list me-2"></i>
            {isAuthenticated ? `${getValidRole(role)?.charAt(0).toUpperCase()}${getValidRole(role)?.slice(1)} Menu` : 'Navigation'}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          <Nav className="flex-column">
            {getMobileNavItems()}
          </Nav>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default Navbar;
